import { handleError } from "@/api/handleError";
import logger from "@/helpers/logger";
import {
  fetchSurrealAuthToken,
  SurrealAuthToken,
} from "@/helpers/queries/activitystream";
import { Activities, Activity } from "@/helpers/types/activity";
import { Centrifuge } from "centrifuge";
import { defineStore } from "pinia";
import { Surreal } from "surrealdb.js";
import { JWTToken } from "./types";

const NS = "default";
const DB = "default";
const SURREAL_HOST = "wss://iris.pressone.co:443/rpc";

interface ActivityState {
  surrealdb: Surreal;
  authToken?: SurrealAuthToken;
  dbConnected: boolean;
  activity?: Activities | undefined;
  queryUuid: string;
  auditReport?:
    | string
    | number
    | boolean
    | symbol
    | Activity
    | Array<Activity[]>
    | null;
  loading: boolean;
  personalToken: string;
  personalChannel: string;
  businessToken: string;
  businessChannel: string;
  centrifuge: Centrifuge | null;
  activityData: Activities;
  fetchedHistory: boolean;
}

export const useActivityStore = defineStore("activities", {
  state: (): ActivityState => ({
    surrealdb: new Surreal(),
    authToken: null,
    dbConnected: false,
    activity: [],
    queryUuid: "",
    auditReport: [],
    loading: false,
    personalToken: "",
    personalChannel: "",
    businessToken: "",
    businessChannel: "",
    centrifuge: null,
    activityData: [],
    fetchedHistory: false,
  }),

  getters: {
    isTokenValid() {
      const decodedJWT = decodeJWT(this.authToken);
      // Check validity
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds since the Unix epoch

      const isValid = decodedJWT?.payload.exp
        ? currentTime < decodedJWT.payload.exp
        : false;

      return isValid;
    },
  },

  actions: {
    async fetchToken() {
      try {
        const token = await fetchSurrealAuthToken().then((res) => {
          return res.data;
        });

        this.authToken = token.token;

        return token.token;
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error fetching surreal auth token",
        });
      }
    },
    async instantiateDB() {
      if (!this.surrealdb) {
        logger.error(
          new Error("No surrealDB instance"),
          "No surrealDB instance"
        );

        return;
      }

      try {
        const token = await this.fetchToken();

        if (!token) {
          logger.error(
            new Error("Invalid surreal token"),
            "Invalid surreal token"
          );

          return;
        }

        await this.surrealdb.connect(SURREAL_HOST, {
          auth: token,
          namespace: NS,
          database: DB,
        });
        await this.surrealdb.info();
        this.dbConnected = true;
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error instantiating surrealDB",
          silent: true,
        });

        this.dbConnected = false;
      }
    },

    async connectDB() {
      if (!this.dbConnected) {
        try {
          await this.instantiateDB();
        } catch (error) {
          handleError({
            error,
            fallbackMessage: "Error connecting to surrealDB",
            silent: true,
          });
        }
      }
    },

    async listenDB() {
      try {
        const result = await this.surrealdb.query(
          "SELECT * FROM activity ORDER BY timestamp DESC"
        );

        // @ts-expect-error This is troublesome to type
        this.auditReport = result;
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error listening to surrealDB",
          silent: true,
        });
      }
    },
  },
});

function decodeJWT(token: SurrealAuthToken) {
  if (!token || typeof token !== "string") {
    logger.error(
      new Error("Invalid or unsupported surreal token"),
      "Invalid or unsupported surreal token"
    );
    return;
  }

  const [headerEncoded, payloadEncoded] = token.split(".");

  // TODO: Get the type of the decoded tokens
  const header = JSON.parse(window.atob(headerEncoded)) as JWTToken;
  const payload = JSON.parse(window.atob(payloadEncoded)) as JWTToken;

  return { header, payload };
}
